import React from 'react';
import { FlagIcon, FlagIconCode } from 'react-flag-kit';
import { defineMessages, useIntl } from 'react-intl';

import { featureStateHooks } from '../../hooks/featureStateHooks/featureStateHooks';
import { navigationHooks } from '../../hooks/navigationHooks/navigationHooks';
import { storeSwitcherHooks } from '../../hooks/storeSwitcherHooks/storeSwitcherHooks';
import { MenuItemPresentational, DropDownMenuPresentational } from '../../types/common';
import { languageUtils } from '../../utils/languageUtils/languageUtils';
import {
  HeaderMenuContentFooterItem,
} from '../HeaderMenuContentFooterItem/HeaderMenuContentFooterItem';
import { Link } from '../Link/Link';
import { Star, CircleWithQuestionMark, Chevron } from '../icons';
import styles from './HeaderMenuContentFooter.module.css';

const messages = defineMessages({
  redeemPageLink: {
    id: 'src.components.HeaderMenuContentFooter.redeemPageLink',
    defaultMessage: 'Choice Redeem Gift Card',
  },
  businessPageLink: {
    id: 'src.components.HeaderMenuContentFooter.businessPageLink',
    defaultMessage: 'Business page',
  },
  customerSupportLink: {
    id: 'src.components.HeaderMenuContentFooter.customerSupportLink',
    defaultMessage: 'Customer support',
  },
  choiceGiftcardLink: {
    id: 'src.components.HeaderMenuContentFooter.choiceGiftcardLink',
    defaultMessage: 'Choice gift card',
  },
  language: {
    id: 'src.components.HeaderMenuContentFooter.language',
    defaultMessage: 'Language',
  },
});

export const LINK_ICON_COLOR = '#fff';

export enum LinkBuild {
  ALL = 'all',
  CB = 'CB',
  CK = 'CK',
}

export enum ElemTypename {
  Accordion = 'Accordion',
  Custom = 'Custom',
  Link = 'Link',
}

export type Link = {
  build: LinkBuild;
  icon: JSX.Element;
  isTargetBlank?: boolean;
  onClick?: React.MouseEventHandler;
  redirect: () => string;
  text: string;
  typename: ElemTypename.Link;
};

export type Accordion = {
  build: LinkBuild;
  dropdownItems?: MenuItemPresentational[];
  icon: JSX.Element;
  onClick?: React.MouseEventHandler;
  redirect: () => string;
  text?: string;
  typename: ElemTypename.Accordion;
};

export type Custom = {
  icon: JSX.Element;
  iconClassName?: string;
  isShow: boolean;
  itemContentClassName?: string;
  onClick?: React.MouseEventHandler;
  rightIcon?: JSX.Element;
  text: string;
  typename: ElemTypename.Custom;
};

export type UseLinksParams = {
  businessMenu?: DropDownMenuPresentational | null;
  isBusinessPageV3: boolean;
  language: string;
};

export const hooks = {
  useLinks({
    language,
    businessMenu,
    isBusinessPageV3,
  }: UseLinksParams): (Link | Custom | Accordion)[] {
    const intl = useIntl();

    const customerSupportRedirect = navigationHooks.useCustomerSupportPageRedirect();
    const businessRedirect = navigationHooks.useBusinessPageRedirect();
    const redeemRedirect = navigationHooks.useRedeemCheckBalanceRedirect();
    const choiceGiftcardRedirect = navigationHooks.useChoiceGiftcardRoute();
    const handleOpenStoreSwitcherModal = storeSwitcherHooks.useOpenStoreSwitcherModal(true);
    const flagCode = hooks.useGetFlagCode(language);
    const { languageSwitcher } = featureStateHooks.useFeatureToggles();

    const businessPageItem: Link | Accordion = isBusinessPageV3
      ? {
        icon: <Star fill={LINK_ICON_COLOR} className={styles.itemIcon} />,
        text: businessMenu?.title,
        redirect: businessRedirect,
        build: LinkBuild.ALL,
        typename: ElemTypename.Accordion,
        dropdownItems: businessMenu?.menuItems,
      }
      : {
        icon: <Star fill={LINK_ICON_COLOR} className={styles.itemIcon} />,
        text: intl.formatMessage(messages.businessPageLink),
        redirect: businessRedirect,
        build: LinkBuild.ALL,
        typename: ElemTypename.Link,
      };

    return [
      {
        icon: <Star fill={LINK_ICON_COLOR} className={styles.itemIcon} />,
        text: intl.formatMessage(messages.redeemPageLink),
        redirect: redeemRedirect,
        build: LinkBuild.ALL,
        typename: ElemTypename.Link,
      },
      {
        icon: <Star fill={LINK_ICON_COLOR} className={styles.itemIcon} />,
        text: intl.formatMessage(messages.choiceGiftcardLink),
        redirect: choiceGiftcardRedirect,
        build: LinkBuild.CB,
        typename: ElemTypename.Link,
      },
      businessPageItem,
      {
        icon: <CircleWithQuestionMark fill={LINK_ICON_COLOR} className={styles.itemIcon} />,
        text: intl.formatMessage(messages.customerSupportLink),
        redirect: customerSupportRedirect,
        build: LinkBuild.ALL,
        typename: ElemTypename.Link,
        isTargetBlank: true,
      },
      {
        icon: (
          <FlagIcon
            code={flagCode.toUpperCase() as FlagIconCode}
            size={16}
            style={{ height: 12 }}
          />
        ),
        text: intl.formatMessage(messages.language),
        onClick: handleOpenStoreSwitcherModal,
        iconClassName: styles.flagIcon,
        typename: ElemTypename.Custom,
        itemContentClassName: styles.itemContentFlag,
        rightIcon: <Chevron fill={LINK_ICON_COLOR} variant='down' />,
        isShow: languageSwitcher,
      },
    ];
  },
  useFilterByBuildCallback() {
    return React.useCallback((link: Link | Custom) => {
      if (link.typename === ElemTypename.Custom) return link;
      if (link.build === 'all') return true;
      return String(link.build) === String(CONFIG.applicationBuild);
    }, []);
  },
  useGetFlagCode(language: string) {
    return React.useMemo(() => {
      const lang = !language ? languageUtils.getDefaultLanguage() : language;
      const flagCode = lang === 'en' ? 'gb' : lang;
      return flagCode.toUpperCase();
    }, [language]);
  },
};

export type Props = {
  businessMenu?: DropDownMenuPresentational | null;
  isBusinessPageV3?: boolean;
  language: string;
};

export const HeaderMenuContentFooter = React.memo((props: Props) => {
  const { language, businessMenu, isBusinessPageV3 = false } = props;
  const links = hooks.useLinks({ language, businessMenu, isBusinessPageV3 });

  const filterLink = hooks.useFilterByBuildCallback();

  return (
    <div className={styles.headerMenuContentFooter}>
      {links.filter(filterLink).map(link => {
        return (
          <HeaderMenuContentFooterItem
            key={link.text}
            itemLink={link}
          />
        );
      })}
    </div>
  );
});

HeaderMenuContentFooter.displayName = 'HeaderMenuContentFooter';
